import type { SettingsTemplateModel } from '@/shared/generated'

import React, { useMemo } from 'react'
import { FeatureFlag } from '@openfeature/react-sdk'
import { useQueryClient } from '@tanstack/react-query'
import {
	EllipsisVerticalIcon,
	GitPullRequestArrowIcon,
	GitPullRequestCreateArrowIcon,
	LayersIcon,
	Trash2Icon,
} from 'lucide-react'
import { Slot } from 'radix-ui'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { ApplyAction } from '@/shared/generated'
import { useModals } from '@/shared/lib/modal-manager'

import {
	useCreateSettingsTemplateMutation,
	useDeleteSettingsTemplateMutation,
} from '../api/mutations'
import { getApplyActions } from '../lib/utils'

export function SettingsTemplateActions({
	template,
}: {
	template: SettingsTemplateModel
}) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button size='icon-sm' variant='ghost'>
					<EllipsisVerticalIcon />
				</Button>
			</DropdownMenuTrigger>
			<SettingsTemplateActionsMenu template={template} />
		</DropdownMenu>
	)
}
const icons = {
	[ApplyAction.UpdatePricingCampaignSettings]: <GitPullRequestArrowIcon />,
	[ApplyAction.ReplacePricingCampaignSettings]: (
		<GitPullRequestCreateArrowIcon />
	),
	[ApplyAction.AddWhatIfScenario]: <GitPullRequestCreateArrowIcon />,
	[ApplyAction.ReplaceMainScenario]: <GitPullRequestArrowIcon />,
	[ApplyAction.UpdateScenarioSettings]: <GitPullRequestCreateArrowIcon />,
}
function SettingsTemplateActionsMenu({
	template,
}: {
	template: SettingsTemplateModel
}) {
	const { t } = useTranslation()

	const applyActions = useMemo(
		() => getApplyActions(template.template_type),
		[template.template_type]
	)

	return (
		<DropdownMenuContent align='end' side='bottom'>
			<DuplicateSettingsTemplate template={template}>
				<DropdownMenuItem>
					<LayersIcon className='size-4' />
					{t('general_duplicate')}
				</DropdownMenuItem>
			</DuplicateSettingsTemplate>
			{applyActions?.map((action) => {
				if (action === ApplyAction.AddWhatIfScenario) {
					return (
						<FeatureFlag key={action} flagKey='core_whatif' defaultValue={true}>
							<ApplySettingsTemplate template={template} action={action}>
								<DropdownMenuItem>
									{icons[action]}
									{t(`${action}`, { defaultValue: action })}
								</DropdownMenuItem>
							</ApplySettingsTemplate>
						</FeatureFlag>
					)
				}

				return (
					<ApplySettingsTemplate
						key={action}
						template={template}
						action={action}
					>
						<DropdownMenuItem>
							{icons[action]}
							{t(`${action}`, { defaultValue: action })}
						</DropdownMenuItem>
					</ApplySettingsTemplate>
				)
			})}
			<DeleteSettingsTemplate template={template}>
				<DropdownMenuItem variant='destructive'>
					<Trash2Icon />
					{t('general_delete', { defaultValue: 'Delete' })}
				</DropdownMenuItem>
			</DeleteSettingsTemplate>
		</DropdownMenuContent>
	)
}

const DuplicateSettingsTemplate = ({
	template,
	children,
}: {
	template: SettingsTemplateModel
	children: React.ReactNode
}) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['settings-templates'],
			})
		},
	})
	const handleDuplicateClick = () => {
		toast.promise(
			createTemplateMutation.mutateAsync({
				name: template.name,
				description: template.description || '',
				body: template.body,
				template_type: template.template_type,
			}),
			{
				loading: t('duplicating.proccess', { defaultValue: 'Duplicating...' }),
				success: t('duplicating.success', {
					defaultValue: 'Duplicated successfully',
				}),
				error: t('duplicating.error', {
					defaultValue: 'Error while duplicating',
				}),
			}
		)
	}

	return <Slot.Root onClick={handleDuplicateClick}>{children}</Slot.Root>
}

const DeleteSettingsTemplate = ({
	template,
	children,
}: {
	template: SettingsTemplateModel
	children: React.ReactNode
}) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const deleteTemplateMutation = useDeleteSettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['settings-templates'],
			})
		},
	})
	const handleDeleteClick = async () => {
		const answer = await confirm({
			title: t('general_detete_confirm_title'),
			description: t('general_detete_confirm_subtitle'),
			actionText: t('general_delete'),
			cancelText: t('general_cancel'),
			ActionProps: {
				variant: 'destructive',
			},
		})
		if (answer) {
			toast.promise(deleteTemplateMutation.mutateAsync(template.id), {
				loading: t('deleting.proccess', { defaultValue: 'Deleting...' }),
				success: t('deleting.success', {
					defaultValue: 'Deleted successfully',
				}),
				error: t('deleting.error', { defaultValue: 'Error while deleting' }),
			})
		}
	}

	return <Slot.Root onClick={handleDeleteClick}>{children}</Slot.Root>
}

const ApplySettingsTemplate = ({
	template,
	children,
	action,
}: {
	template: SettingsTemplateModel
	children: React.ReactNode
	action: ApplyAction
}) => {
	const { showModal } = useModals()
	const handleApplyClick = () => {
		showModal('SETTINGS_TEMPLATE_APPLY_MODAL', {
			template,
			action,
		})
	}

	return <Slot.Root onClick={handleApplyClick}>{children}</Slot.Root>
}
